import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useConfetti } from './useConfetti';

// Simple global state for celebration
let globalCelebration: string | null = null;
const listeners = new Set<() => void>();

function setCelebration(value: string | null) {
  globalCelebration = value;
  listeners.forEach(l => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return globalCelebration;
}

export type AchievementType = 
  | 'first_project'
  | 'first_task'
  | 'first_proposal'
  | 'first_client'
  | 'first_financial'
  | 'first_diario'
  | 'first_voice'
  | 'first_ocr'
  | 'first_webhook'
  | 'first_share'
  | 'five_projects'
  | 'ten_tasks'
  | 'project_completed'
  | 'foundation_completed'
  | 'structure_50'
  | 'structure_completed'
  | 'roof_completed'
  | 'finishing_started';

export interface Achievement {
  id: string;
  user_id: string;
  achievement_type: AchievementType;
  achieved_at: string;
}

export const ACHIEVEMENT_CONFIG: Record<AchievementType, { 
  title: string; 
  description: string; 
  emoji: string;
  points: number;
}> = {
  first_project: { title: 'Primeira Obra!', description: 'Você criou seu primeiro projeto', emoji: '🏗️', points: 20 },
  first_task: { title: 'Mão na Massa!', description: 'Você criou sua primeira tarefa no cronograma', emoji: '✅', points: 10 },
  first_proposal: { title: 'Vendedor Nato!', description: 'Você criou sua primeira proposta', emoji: '📄', points: 15 },
  first_client: { title: 'Carteira Cheia!', description: 'Você cadastrou seu primeiro cliente', emoji: '👤', points: 15 },
  first_financial: { title: 'Controlador!', description: 'Você registrou sua primeira movimentação financeira', emoji: '💰', points: 15 },
  first_diario: { title: 'Documentador!', description: 'Você criou seu primeiro diário de obra', emoji: '📝', points: 20 },
  first_voice: { title: 'Voz Ativa!', description: 'Você usou gravação de voz pela primeira vez', emoji: '🎤', points: 25 },
  first_ocr: { title: 'Scanner Pro!', description: 'Você extraiu dados de uma imagem com OCR', emoji: '📷', points: 25 },
  first_webhook: { title: 'Integrado!', description: 'Você configurou seu primeiro webhook', emoji: '🔗', points: 30 },
  first_share: { title: 'Influenciador!', description: 'Você compartilhou o portal do cliente', emoji: '🚀', points: 20 },
  five_projects: { title: 'Construtor!', description: 'Você tem 5 projetos cadastrados', emoji: '🏆', points: 50 },
  ten_tasks: { title: 'Organizador!', description: 'Você criou 10 tarefas', emoji: '📋', points: 30 },
  project_completed: { title: 'Missão Cumprida!', description: 'Você completou um projeto', emoji: '🎉', points: 100 },
  foundation_completed: { title: 'Fundação Concluída!', description: 'A base da obra está pronta', emoji: '🧱', points: 40 },
  structure_50: { title: 'Estrutura 50%!', description: 'Metade da estrutura concluída', emoji: '🏗️', points: 30 },
  structure_completed: { title: 'Estrutura Completa!', description: 'Toda a estrutura está de pé', emoji: '🏛️', points: 50 },
  roof_completed: { title: 'Cobertura Finalizada!', description: 'A obra já tem telhado', emoji: '🏠', points: 45 },
  finishing_started: { title: 'Acabamento Iniciado!', description: 'Fase final da obra começou', emoji: '✨', points: 35 },
};

export function useAhamMoments() {
  const { user } = useAuth();
  const { fireCelebration } = useConfetti();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Global state for celebration
  const pendingCelebration = useSyncExternalStore(subscribe, getSnapshot) as AchievementType | null;

  useEffect(() => {
    if (user) {
      fetchAchievements();
    }
  }, [user]);

  const fetchAchievements = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id);
      if (error) throw error;
      setAchievements((data as unknown as Achievement[]) || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasAchievement = useCallback((type: AchievementType) => {
    return achievements.some(a => a.achievement_type === type);
  }, [achievements]);

  const unlockAchievement = useCallback(async (type: AchievementType) => {
    if (!user || hasAchievement(type)) return false;
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .insert({ user_id: user.id, achievement_type: type })
        .select()
        .single();
      if (error) {
        if (error.code === '23505') return false;
        throw error;
      }
      setAchievements(prev => [...prev, data as unknown as Achievement]);
      setCelebration(type);
      fireCelebration();
      return true;
    } catch (error) {
      console.error('Error unlocking achievement:', error);
      return false;
    }
  }, [user, hasAchievement, fireCelebration]);

  const clearCelebration = useCallback(() => {
    setCelebration(null);
  }, []);

  const getTotalPoints = useCallback(() => {
    return achievements.reduce((total, a) => {
      const config = ACHIEVEMENT_CONFIG[a.achievement_type as AchievementType];
      return total + (config?.points || 0);
    }, 0);
  }, [achievements]);

  const getCompletionPercentage = useCallback(() => {
    const totalAchievements = Object.keys(ACHIEVEMENT_CONFIG).length;
    return Math.round((achievements.length / totalAchievements) * 100);
  }, [achievements]);

  const checkMilestoneAchievements = useCallback(async () => {
    if (!user) return;
    try {
      const { count: projectCount } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (projectCount && projectCount >= 5 && !hasAchievement('five_projects')) {
        await unlockAchievement('five_projects');
      }
      const { count: taskCount } = await supabase
        .from('project_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (taskCount && taskCount >= 10 && !hasAchievement('ten_tasks')) {
        await unlockAchievement('ten_tasks');
      }
    } catch (error) {
      console.error('Error checking milestone achievements:', error);
    }
  }, [user, hasAchievement, unlockAchievement]);

  return {
    achievements,
    loading,
    hasAchievement,
    unlockAchievement,
    pendingCelebration,
    clearCelebration,
    getTotalPoints,
    getCompletionPercentage,
    checkMilestoneAchievements,
    refetch: fetchAchievements,
  };
}
